<template>
	<AppLayout
		:title="trans('enrollments.packs.create.title')"
		:subtitle="trans('enrollments.packs.create.subtitle')"
		:return-url="route('events.enrollments.show', enrollment.id)"
		:return-label="trans('enrollments.id', {id: enrollment.id})"
	>
		<p class="text-gray-400 text-sm">{{ trans('enrollments.create.help') }}</p>
		<Alert v-if="!registrationStage.refundable" :color="Color.Blue">{{ trans('enrollments.not_refundable') }}</Alert>
		<div class="grid grid-cols-1 md:grid-cols-9 gap-4">
			<div class="md:col-span-5 lg:col-span-6 flex flex-col gap-4">
				<Empty v-if="packs.length === 0"/>
				<SelectPack v-for="pack in packs" :key="`pack-${pack.id}`" :pack="pack" :selected-packs="selectedPacks" @selected="select"/>
			</div>
			<div class="md:col-span-4 lg:col-span-3">
				<Card class="sticky top-16" :title="trans('enrollments.summary.title')">
					<div v-for="pack in selectedPacks" class="flex">
						<p class="grow">{{ pack.title }}</p>
						<p>$ {{ formatNumber(pack.price, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</p>
					</div>
					<hr>
					<div class="flex font-bold">
						<p class="grow">{{ trans('enrollments.invoice.total') }}</p>
						<p>$ {{ formatNumber(total, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</p>
					</div>
					<p v-if="!canProceed" class="text-gray-400">{{ trans('enrollments.cannot_proceed') }}</p>
					<PrimaryButton :color="Color.Green" data-testid="proceed-button" :disabled="!canProceed" @click.stop="proceed">
						<i class="fa-solid fa-check mr-2"/>
						{{ trans('general.proceed') }}
					</PrimaryButton>
				</Card>
			</div>
		</div>
	</AppLayout>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {useForm} from '@inertiajs/vue3'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/Empty.vue'
import Card from '@/Components/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Alert from '@/Components/Alerts/Alert.vue'
import {type Pack} from '@/Types/Events/Packs/Pack'
import {computed, type ComputedRef, ref, type Ref} from 'vue'
import SelectPack from '@/Components/Enrollments/SelectPack.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import {type Enrollment} from '@/Types/Events/Enrollments/Enrollment'
import {type RegistrationStage} from '@/Types/Events/Stages/RegistrationStage'
import {formatNumber, showInfo, showSuccess, showValidationErrors} from '@/helpers'

const props = defineProps<{
	packs:Array<Pack>,
	enrollment:Enrollment,
	selectedPacks:Array<Pack>,
	registrationStage:RegistrationStage,
}>()

const sending:Ref<boolean> = ref(false)
const packs:Ref<Array<Pack>> = ref(props.packs)
const enrollment:Ref<Enrollment> = ref(props.enrollment)
const selectedPacks:Ref<Array<Pack>> = ref(props.selectedPacks)
const registrationStage:Ref<RegistrationStage> = ref(props.registrationStage)


const subtotal:ComputedRef<number> = computed(() => {
	let subtotal = 0
	for(let i = 0; i < selectedPacks.value.length; i++){
		subtotal += selectedPacks.value[i].price
	}
	return subtotal
})
const total:ComputedRef<number> = computed(() => {
	return subtotal.value
})
const canProceed:ComputedRef<boolean> = computed(() => {
	if(registrationStage.value.selection_mandatory){
		return selectedPacks.value.filter((p:Pack) => !p.mandatory).length > 0
	}
	return selectedPacks.value.length > 0
})

function select(pack:Pack):void{
	if(props.registrationStage.selection_type === 'single'){
		if(!pack.mandatory){
			let previousSelectedIndex = -1
			let previousSelectedId = null
			if(selectedPacks.value.length > 0){
				previousSelectedIndex = selectedPacks.value.findIndex(selectedPack => !selectedPack.mandatory)
				if(previousSelectedIndex > -1){
					previousSelectedId = selectedPacks.value[previousSelectedIndex].id
					selectedPacks.value.splice(previousSelectedIndex, 1)
				}
			}
			if(props.registrationStage.selection_mandatory || previousSelectedId !== pack.id){
				selectedPacks.value.push(pack)
			}
		}
	} else {
		toggle(pack)
	}
}

function toggle(pack:Pack):void{
	const index = selectedPacks.value.findIndex((p) => p.id === pack.id)
	if(index === -1){
		selectedPacks.value.push(pack)
	} else {
		if(!selectedPacks.value[index].mandatory){
			selectedPacks.value.splice(index, 1)
		}
	}
}

function proceed():void{
	if(!sending.value && canProceed.value){
		showInfo(trans('general.updating'))
		sending.value = true
		useForm({
			packs: selectedPacks.value.map((pack:Pack) => pack.id),
		}).post(route('events.enrollments.packs.store', enrollment.value.id), {
			onSuccess: () => {
				showSuccess(trans('general.updated'))
			},
			onError: showValidationErrors,
			onFinish: () => {
				sending.value = false
			},
		})
	}
}
</script>
